import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../store/store";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

export const enumToTitleCase = (input: string) => {
  if (!input) return "";

  // Replace underscores with spaces
  // Capitalize the first letter of each word
  return input
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const toCamelCase = (input: string): string => {
  if (!input) return "";

  return input
    .toLowerCase()
    .replace(/[_\s]+(.)?/g, (_, chr) => (chr ? chr.toUpperCase() : ""));
};

export const stripPrefixIncl = (
  text: string | undefined,
  prefix: string
): string => {
  if (!text) return "";
  const regex = new RegExp(`.*${prefix}`);
  return text.replace(regex, "").toUpperCase();
};

export const getPieChartUsage = (
  usage: number,
  dailyLimit?: number
): number => {
  if (dailyLimit) {
    const usagePercentage = (usage / dailyLimit) * 100;
    const remainingPercentage = 100 - usagePercentage;

    return usagePercentage;
    // return {
    //   usage: usagePercentage,
    //   remaining: remainingPercentage >= 0 ? remainingPercentage : 0,
    // };
  } else {
    // No daily limit, the pie chart should only reflect the usage
    return usage > 100 ? 100 : usage;
  }
};

export const getDashboardDeviceCount = (
  filterDropdown: { label: string; value: string[] }[],
  metrics: Record<string, number>
): Record<string, number> => {
  const result: Record<string, number> = {};

  filterDropdown.forEach(({ label, value }) => {
    const formattedLabel = label.toLowerCase().replace(/\s+/g, "_");
    result[formattedLabel] = value.reduce((total, key) => {
      return total + (metrics[key] || 0);
    }, 0);
  });

  return result;
};

type CategoryKey =
  | "devicesDown"
  | "weakSignal"
  | "lowBattery"
  | "leaking"
  | "zeroCount";

const labelMap: Record<CategoryKey, string> = {
  devicesDown: "Devices Down",
  weakSignal: "Weak Signal",
  lowBattery: "Low Battery",
  leaking: "Leaking",
  zeroCount: "Zero Count",
};

export function deepSearch(data: any): {
  dropdownOptions: { label: string; value: string[] }[];
  allDevices: string[];
} {
  const result: Record<CategoryKey, Set<string>> = {
    devicesDown: new Set(),
    weakSignal: new Set(),
    lowBattery: new Set(),
    leaking: new Set(),
    zeroCount: new Set(),
  };

  function traverse(obj: any) {
    if (typeof obj !== "object" || obj === null) return;

    for (const key in obj) {
      const value = obj[key];

      if (Object.keys(labelMap).includes(key)) {
        const categoryKey = key as CategoryKey;
        if (value?.devices?.length) {
          value.devices.forEach((device: string) =>
            result[categoryKey].add(device)
          );
        } else if (Array.isArray(value)) {
          value.forEach((device: string) => result[categoryKey].add(device));
        }
      }

      traverse(value);
    }
  }

  traverse(data);

  const dropdownOptions = Object.entries(result).map(([key, set]) => ({
    label: labelMap[key as CategoryKey],
    value: Array.from(set),
  }));

  const allDevices = Array.from(
    new Set(dropdownOptions.flatMap((option) => option.value))
  );

  return { dropdownOptions, allDevices };
}
